import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { leaveApiClient } from './apiClient';

/**
 * Local simulation store key for seamless offline development / testing when backend is not yet started
 */
const STORAGE_KEY = 'paybyte_leave_management_store';

const getInitialStore = () => {
  return {
    orgLeaveTypes: [
      { leave_type_id: 1, org_id: 1, leave_type: 'Sick Leave', active: true },
      { leave_type_id: 2, org_id: 1, leave_type: 'Casual Leave', active: true },
      { leave_type_id: 3, org_id: 1, leave_type: 'Privilege Leave', active: true },
    ],
    orgLeaveCounts: [
      { org_leave_id: 1, org_id: 1, leave_type_id: 1, leave_type: 'Sick Leave', leave_count: 12, active: true },
      { org_leave_id: 2, org_id: 1, leave_type_id: 2, leave_type: 'Casual Leave', leave_count: 10, active: true },
      { org_leave_id: 3, org_id: 1, leave_type_id: 3, leave_type: 'Privilege Leave', leave_count: 15, active: true },
    ],
    employeeLeaveCounts: [
      { org_user_leave_id: 1, user_id: 5, org_id: 1, leave_type_id: 1, leave_type: 'Sick Leave', total_leave_count: 10, active: true },
      { org_user_leave_id: null, user_id: 5, org_id: 1, leave_type_id: 2, leave_type: 'Casual Leave', total_leave_count: 0, active: true },
      { org_user_leave_id: 3, user_id: 5, org_id: 1, leave_type_id: 3, leave_type: 'Privilege Leave', total_leave_count: 15, active: true },
      { org_user_leave_id: 4, user_id: 101, org_id: 1, leave_type_id: 1, leave_type: 'Sick Leave', total_leave_count: 12, active: true },
      { org_user_leave_id: 5, user_id: 101, org_id: 1, leave_type_id: 2, leave_type: 'Casual Leave', total_leave_count: 10, active: true },
      { org_user_leave_id: 6, user_id: 102, org_id: 1, leave_type_id: 1, leave_type: 'Sick Leave', total_leave_count: 15, active: true },
    ],
    appliedLeaves: [
      {
        leave_id: 1,
        org_id: 1,
        user_id: 5,
        from_date: '2026-10-01',
        to_date: '2026-10-07',
        leave_type_id: 1,
        leave_type: 'Sick Leave',
        applied_leave: 7,
        status: 'APPLIED',
        active: true,
      },
      {
        leave_id: 2,
        org_id: 1,
        user_id: 101,
        from_date: '2026-09-10',
        to_date: '2026-09-12',
        leave_type_id: 2,
        leave_type: 'Casual Leave',
        applied_leave: 3,
        status: 'APPROVED',
        active: true,
      },
    ],
  };
};

const getStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  const initial = getInitialStore();
  saveStore(initial);
  return initial;
};

const saveStore = (store) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    // ignore
  }
};

/**
 * Check whether an error is a network connection error (i.e. server not running or unreachable)
 */
const isNetworkOffline = (err) => {
  return !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED';
};

export const leaveService = {
  /**
   * 1. Get Org Leave Types
   * GET /{orgId}/leave-types
   */
  getLeaveTypes: async (orgId) => {
    const numericOrgId = Number(orgId);
    try {
      const response = await leaveApiClient.get(API_ENDPOINTS.leaves.leaveTypes(numericOrgId));
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        return store.orgLeaveTypes.filter((t) => Number(t.org_id) === numericOrgId);
      }
      throw err;
    }
  },

  /**
   * 1. Create Org Leave Type
   * POST /{orgId}/leave-types
   * Body: { org_id: 1, leave_type: "Sick Leave" }
   */
  createLeaveType: async (orgId, leaveType) => {
    const numericOrgId = Number(orgId);
    const payload = {
      org_id: numericOrgId,
      leave_type: leaveType?.trim(),
    };

    try {
      const response = await leaveApiClient.post(
        API_ENDPOINTS.leaves.leaveTypes(numericOrgId),
        payload
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const existing = store.orgLeaveTypes.find(
          (t) =>
            Number(t.org_id) === numericOrgId &&
            t.leave_type.trim().toLowerCase() === payload.leave_type.toLowerCase()
        );
        if (existing) {
          const duplicateErr = new Error('Duplicate leave_type for the same org returns 400 Bad Request.');
          duplicateErr.response = { status: 400, data: { message: 'Duplicate leave_type for the same org.' } };
          duplicateErr.userMessage = 'Duplicate leave_type for the same org returns 400 Bad Request.';
          throw duplicateErr;
        }

        const newId = store.orgLeaveTypes.length > 0 ? Math.max(...store.orgLeaveTypes.map((t) => t.leave_type_id)) + 1 : 1;
        const newRecord = {
          leave_type_id: newId,
          org_id: numericOrgId,
          leave_type: payload.leave_type,
          active: true,
        };
        store.orgLeaveTypes.push(newRecord);
        saveStore(store);
        return newRecord;
      }
      throw err;
    }
  },

  /**
   * 2. Get Org-Level Leave Count
   * GET /{orgId}/{leaveTypeId}
   */
  getOrgLeaveCount: async (orgId, leaveTypeId) => {
    const numericOrgId = Number(orgId);
    const numericLeaveTypeId = Number(leaveTypeId);

    try {
      const response = await leaveApiClient.get(
        API_ENDPOINTS.leaves.orgLeaveCount(numericOrgId, numericLeaveTypeId)
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const found = store.orgLeaveCounts.find(
          (c) => Number(c.org_id) === numericOrgId && Number(c.leave_type_id) === numericLeaveTypeId
        );
        if (found) return found;

        const lt = store.orgLeaveTypes.find(
          (t) => Number(t.org_id) === numericOrgId && Number(t.leave_type_id) === numericLeaveTypeId
        );
        if (!lt) {
          const notFoundErr = new Error('Unknown org or leave type returns 404 Not Found.');
          notFoundErr.response = { status: 404, data: { message: 'Not Found' } };
          notFoundErr.userMessage = 'Unknown org or leave type returns 404 Not Found.';
          throw notFoundErr;
        }
        return {
          org_leave_id: 0,
          org_id: numericOrgId,
          leave_type_id: numericLeaveTypeId,
          leave_type: lt.leave_type,
          leave_count: 0,
          active: true,
        };
      }
      throw err;
    }
  },

  /**
   * 2. Create Or Update Org-Level Leave Count
   * POST /{orgId}/{leaveTypeId}
   * Body: { org_id: 1, leave_type_id: 1, leave_count: 12 }
   */
  setOrgLeaveCount: async (orgId, leaveTypeId, leaveCount) => {
    const numericOrgId = Number(orgId);
    const numericLeaveTypeId = Number(leaveTypeId);
    const numericCount = Number(leaveCount);

    if (numericCount < 0) {
      const invalidErr = new Error('leave_count must be greater than or equal to 0.');
      invalidErr.response = { status: 400, data: { message: 'leave_count must be greater than or equal to 0.' } };
      invalidErr.userMessage = 'leave_count must be greater than or equal to 0.';
      throw invalidErr;
    }

    const payload = {
      org_id: numericOrgId,
      leave_type_id: numericLeaveTypeId,
      leave_count: numericCount,
    };

    try {
      const response = await leaveApiClient.post(
        API_ENDPOINTS.leaves.orgLeaveCount(numericOrgId, numericLeaveTypeId),
        payload
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const lt = store.orgLeaveTypes.find(
          (t) => Number(t.org_id) === numericOrgId && Number(t.leave_type_id) === numericLeaveTypeId
        );
        if (!lt) {
          const notFoundErr = new Error('Unknown org or leave type returns 404 Not Found.');
          notFoundErr.response = { status: 404, data: { message: 'Not Found' } };
          notFoundErr.userMessage = 'Unknown org or leave type returns 404 Not Found.';
          throw notFoundErr;
        }

        const existingIdx = store.orgLeaveCounts.findIndex(
          (c) => Number(c.org_id) === numericOrgId && Number(c.leave_type_id) === numericLeaveTypeId
        );
        let updated;
        if (existingIdx >= 0) {
          updated = {
            ...store.orgLeaveCounts[existingIdx],
            leave_count: numericCount,
            leave_type: lt.leave_type,
          };
          store.orgLeaveCounts[existingIdx] = updated;
        } else {
          const newId = store.orgLeaveCounts.length > 0 ? Math.max(...store.orgLeaveCounts.map((c) => c.org_leave_id)) + 1 : 1;
          updated = {
            org_leave_id: newId,
            org_id: numericOrgId,
            leave_type_id: numericLeaveTypeId,
            leave_type: lt.leave_type,
            leave_count: numericCount,
            active: true,
          };
          store.orgLeaveCounts.push(updated);
        }
        saveStore(store);
        return updated;
      }
      throw err;
    }
  },

  /**
   * 3. Get Employee Leave Count By Leave Type
   * GET /{orgId}/{userId}/{leaveTypeId}
   */
  getEmployeeLeaveCount: async (orgId, userId, leaveTypeId) => {
    const numericOrgId = Number(orgId);
    const numericUserId = Number(userId);
    const numericLeaveTypeId = Number(leaveTypeId);

    try {
      const response = await leaveApiClient.get(
        API_ENDPOINTS.leaves.employeeLeaveCount(numericOrgId, numericUserId, numericLeaveTypeId)
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const found = store.employeeLeaveCounts.find(
          (c) =>
            Number(c.org_id) === numericOrgId &&
            Number(c.user_id) === numericUserId &&
            Number(c.leave_type_id) === numericLeaveTypeId
        );
        if (found) return found;

        const lt = store.orgLeaveTypes.find(
          (t) => Number(t.org_id) === numericOrgId && Number(t.leave_type_id) === numericLeaveTypeId
        );
        return {
          org_user_leave_id: null,
          user_id: numericUserId,
          org_id: numericOrgId,
          leave_type_id: numericLeaveTypeId,
          leave_type: lt?.leave_type || 'Unknown Leave',
          total_leave_count: 0,
          active: true,
        };
      }
      throw err;
    }
  },

  /**
   * 3. Create Or Update Employee-Level Leave Count
   * POST /{orgId}/{userId}/{leaveTypeId}
   * Body: { user_id: 5, org_id: 1, leave_type_id: 1, total_leave_count: 10 }
   */
  setEmployeeLeaveCount: async (orgId, userId, leaveTypeId, totalLeaveCount) => {
    const numericOrgId = Number(orgId);
    const numericUserId = Number(userId);
    const numericLeaveTypeId = Number(leaveTypeId);
    const numericCount = Number(totalLeaveCount);

    if (numericCount < 0) {
      const invalidErr = new Error('total_leave_count must be greater than or equal to 0.');
      invalidErr.response = { status: 400, data: { message: 'total_leave_count must be greater than or equal to 0.' } };
      invalidErr.userMessage = 'total_leave_count must be greater than or equal to 0.';
      throw invalidErr;
    }

    const payload = {
      user_id: numericUserId,
      org_id: numericOrgId,
      leave_type_id: numericLeaveTypeId,
      total_leave_count: numericCount,
    };

    try {
      const response = await leaveApiClient.post(
        API_ENDPOINTS.leaves.employeeLeaveCount(numericOrgId, numericUserId, numericLeaveTypeId),
        payload
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const lt = store.orgLeaveTypes.find(
          (t) => Number(t.org_id) === numericOrgId && Number(t.leave_type_id) === numericLeaveTypeId
        );
        if (!lt) {
          const notFoundErr = new Error('Unknown user, org, or leave type returns 404 Not Found.');
          notFoundErr.response = { status: 404, data: { message: 'Not Found' } };
          notFoundErr.userMessage = 'Unknown user, org, or leave type returns 404 Not Found.';
          throw notFoundErr;
        }

        const idx = store.employeeLeaveCounts.findIndex(
          (c) =>
            Number(c.org_id) === numericOrgId &&
            Number(c.user_id) === numericUserId &&
            Number(c.leave_type_id) === numericLeaveTypeId
        );

        let updated;
        if (idx >= 0) {
          updated = {
            ...store.employeeLeaveCounts[idx],
            org_user_leave_id: store.employeeLeaveCounts[idx].org_user_leave_id || Date.now(),
            total_leave_count: numericCount,
            leave_type: lt.leave_type,
          };
          store.employeeLeaveCounts[idx] = updated;
        } else {
          const newId = Date.now();
          updated = {
            org_user_leave_id: newId,
            user_id: numericUserId,
            org_id: numericOrgId,
            leave_type_id: numericLeaveTypeId,
            leave_type: lt.leave_type,
            total_leave_count: numericCount,
            active: true,
          };
          store.employeeLeaveCounts.push(updated);
        }
        saveStore(store);
        return updated;
      }
      throw err;
    }
  },

  /**
   * 3. Get All Leave Counts For Employee
   * GET /{orgId}/{userId}
   * Returns list of leave types with their total_leave_count for this employee
   */
  getEmployeeAllLeaveCounts: async (orgId, userId) => {
    const numericOrgId = Number(orgId);
    const numericUserId = Number(userId);

    try {
      const response = await leaveApiClient.get(
        API_ENDPOINTS.leaves.employeeAllLeaveCounts(numericOrgId, numericUserId)
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();
        const orgTypes = store.orgLeaveTypes.filter((t) => Number(t.org_id) === numericOrgId);
        
        return orgTypes.map((type) => {
          const empRecord = store.employeeLeaveCounts.find(
            (c) =>
              Number(c.org_id) === numericOrgId &&
              Number(c.user_id) === numericUserId &&
              Number(c.leave_type_id) === Number(type.leave_type_id)
          );

          if (empRecord) {
            return {
              org_user_leave_id: empRecord.org_user_leave_id,
              user_id: numericUserId,
              org_id: numericOrgId,
              leave_type_id: type.leave_type_id,
              leave_type: type.leave_type,
              total_leave_count: empRecord.total_leave_count,
              active: true,
            };
          }

          return {
            org_user_leave_id: null,
            user_id: numericUserId,
            org_id: numericOrgId,
            leave_type_id: type.leave_type_id,
            leave_type: type.leave_type,
            total_leave_count: 0,
            active: true,
          };
        });
      }
      throw err;
    }
  },

  /**
   * 4. Apply Leave
   * POST /{orgId}/{userId}/leave/apply
   * Body: {
   *   org_id: 1,
   *   user_id: 5,
   *   from_date: "2026-10-01",
   *   to_date: "2026-10-07",
   *   leave_type_id: 1,
   *   applied_leave: 7,
   *   status: "applied"
   * }
   * Supported aliases: from_date/start_date, to_date/end_date, applied_leave/aplied_leave
   * Supported status: applied, approved, rejected, on-hold
   */
  applyLeave: async (orgId, userId, payload) => {
    const numericOrgId = Number(orgId);
    const numericUserId = Number(userId);

    const fromDate = payload.from_date || payload.start_date || payload.startDate;
    const toDate = payload.to_date || payload.end_date || payload.endDate;
    const leaveTypeId = Number(payload.leave_type_id || payload.leaveTypeId);
    const appliedLeave = Number(payload.applied_leave || payload.aplied_leave || payload.appliedLeave);
    const status = (payload.status || 'applied').toLowerCase();

    // Client-side validations matching API Spec
    if (!fromDate) {
      const err = new Error('from_date is required.');
      err.response = { status: 400, data: { message: 'from_date is required.' } };
      err.userMessage = 'from_date is required.';
      throw err;
    }
    if (!toDate) {
      const err = new Error('to_date is required.');
      err.response = { status: 400, data: { message: 'to_date is required.' } };
      err.userMessage = 'to_date is required.';
      throw err;
    }
    if (new Date(toDate) < new Date(fromDate)) {
      const err = new Error('to_date must be on or after from_date.');
      err.response = { status: 400, data: { message: 'to_date must be on or after from_date.' } };
      err.userMessage = 'to_date must be on or after from_date.';
      throw err;
    }
    if (!leaveTypeId) {
      const err = new Error('leave_type_id is required.');
      err.response = { status: 400, data: { message: 'leave_type_id is required.' } };
      err.userMessage = 'leave_type_id is required.';
      throw err;
    }
    if (!appliedLeave || appliedLeave <= 0) {
      const err = new Error('applied_leave must be greater than 0.');
      err.response = { status: 400, data: { message: 'applied_leave must be greater than 0.' } };
      err.userMessage = 'applied_leave must be greater than 0.';
      throw err;
    }

    const requestBody = {
      org_id: numericOrgId,
      user_id: numericUserId,
      from_date: fromDate,
      to_date: toDate,
      leave_type_id: leaveTypeId,
      applied_leave: appliedLeave,
      status: status,
    };

    try {
      const response = await leaveApiClient.post(
        API_ENDPOINTS.leaves.apply(numericOrgId, numericUserId),
        requestBody
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();

        // Check leave type exists
        const lt = store.orgLeaveTypes.find(
          (t) => Number(t.org_id) === numericOrgId && Number(t.leave_type_id) === leaveTypeId
        );
        if (!lt) {
          const err404 = new Error('Leave type must be configured for the employee.');
          err404.response = { status: 404, data: { message: 'Leave type not found.' } };
          err404.userMessage = 'Leave type must be configured for the employee.';
          throw err404;
        }

        // Check entitlement overflow:
        // "For applied, approved, and on-hold, total used leave cannot exceed employee entitlement. Exceeding entitlement returns 409 Conflict. rejected does not consume entitlement."
        const empCountRecord = store.employeeLeaveCounts.find(
          (c) =>
            Number(c.org_id) === numericOrgId &&
            Number(c.user_id) === numericUserId &&
            Number(c.leave_type_id) === leaveTypeId
        );
        const entitlement = empCountRecord ? empCountRecord.total_leave_count : 0;

        if (['applied', 'approved', 'on-hold'].includes(status)) {
          const alreadyUsed = store.appliedLeaves
            .filter(
              (l) =>
                Number(l.org_id) === numericOrgId &&
                Number(l.user_id) === numericUserId &&
                Number(l.leave_type_id) === leaveTypeId &&
                ['APPLIED', 'APPROVED', 'ON-HOLD'].includes(l.status?.toUpperCase())
            )
            .reduce((sum, item) => sum + Number(item.applied_leave || 0), 0);

          if (alreadyUsed + appliedLeave > entitlement) {
            const err409 = new Error(
              `Entitlement overflow: Total used leave (${alreadyUsed + appliedLeave}) exceeds employee entitlement (${entitlement}). Returns 409 Conflict.`
            );
            err409.response = {
              status: 409,
              data: {
                message: `Total used leave cannot exceed employee entitlement (${entitlement} days).`,
              },
            };
            err409.userMessage = `Entitlement overflow: Total used leave exceeds employee entitlement (${entitlement} days). 409 Conflict.`;
            throw err409;
          }
        }

        const newLeaveId = store.appliedLeaves.length > 0 ? Math.max(...store.appliedLeaves.map((l) => l.leave_id)) + 1 : 1;
        const newLeave = {
          leave_id: newLeaveId,
          org_id: numericOrgId,
          user_id: numericUserId,
          from_date: fromDate,
          to_date: toDate,
          leave_type_id: leaveTypeId,
          leave_type: lt.leave_type,
          applied_leave: appliedLeave,
          status: status.toUpperCase(),
          active: true,
        };

        store.appliedLeaves.unshift(newLeave);
        saveStore(store);
        return newLeave;
      }
      throw err;
    }
  },

  /**
   * 4. Get Applied Leaves
   * GET /{orgId}/{userId}/leave/
   * Query params: fromDate=2026-10-01&toDate=2026-12-31
   * Default behavior: If no date range is provided, returns leaves from January 1 to December 31 of current year.
   */
  getAppliedLeaves: async (orgId, userId, options = {}) => {
    const numericOrgId = Number(orgId);
    const numericUserId = Number(userId);

    const params = {};
    if (options.fromDate) params.fromDate = options.fromDate;
    if (options.toDate) params.toDate = options.toDate;

    // Validate if explicit date range provided that toDate >= fromDate
    if (options.fromDate && options.toDate && new Date(options.toDate) < new Date(options.fromDate)) {
      const err = new Error('toDate must be on or after fromDate.');
      err.response = { status: 400, data: { message: 'toDate must be on or after fromDate.' } };
      err.userMessage = 'toDate must be on or after fromDate. (400 Bad Request)';
      throw err;
    }

    try {
      const response = await leaveApiClient.get(
        API_ENDPOINTS.leaves.applied(numericOrgId, numericUserId),
        { params }
      );
      return response.data;
    } catch (err) {
      if (isNetworkOffline(err)) {
        const store = getStore();

        // Default behavior according to spec:
        // "If no date range is provided, returns leaves from January 1 to December 31 of the current year."
        const currentYear = new Date().getFullYear();
        const effectiveFrom = options.fromDate || `${currentYear}-01-01`;
        const effectiveTo = options.toDate || `${currentYear}-12-31`;

        return store.appliedLeaves.filter((l) => {
          if (Number(l.org_id) !== numericOrgId) return false;
          if (Number(l.user_id) !== numericUserId) return false;

          const leaveFrom = l.from_date;
          const leaveTo = l.to_date;

          // Check date overlap
          return leaveTo >= effectiveFrom && leaveFrom <= effectiveTo;
        });
      }
      throw err;
    }
  },

  /**
   * Helper to reset the offline mock store back to clean spec sample data
   */
  resetOfflineStore: () => {
    const initial = getInitialStore();
    saveStore(initial);
    return initial;
  },
};

export default leaveService;
